import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import OnboardingTour from "@/components/OnboardingTour";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ChartBar,
  ListChecks,
  Notebook,
  Receipt,
  Bank,
  ChartLine,
  Buildings,
  Users,
  StackSimple,
  SignOut,
  CaretDown,
  BellRinging,
  Gear,
  Vault,
  Handshake,
  UserCircleGear,
  UsersThree,
  Truck,
  ShieldCheck,
  ShieldStar,
  Sparkle,
  IdentificationCard,
  MoneyWavy,
} from "@phosphor-icons/react";

const NAV = [
  { to: "/dashboard", label: "Overview", icon: ChartBar, testId: "nav-overview" },
  { to: "/accounts", label: "Chart of Accounts", icon: ListChecks, testId: "nav-accounts" },
  { to: "/journals", label: "Journal Entries", icon: Notebook, testId: "nav-journals" },
  { to: "/invoices", label: "Invoices & Bills", icon: Receipt, testId: "nav-invoices" },
  { to: "/customers", label: "Customers", icon: UsersThree, testId: "nav-customers" },
  { to: "/suppliers", label: "Suppliers", icon: Truck, testId: "nav-suppliers" },
  { to: "/expenses", label: "Expenses", icon: MoneyWavy, testId: "nav-expenses" },
  { to: "/employees", label: "Employees", icon: IdentificationCard, testId: "nav-employees" },
  { to: "/bank-accounts", label: "Bank Accounts", icon: Vault, testId: "nav-bank-accounts" },
  { to: "/bank", label: "Bank Holding", icon: Bank, testId: "nav-bank" },
  { to: "/reminders", label: "Payment Reminders", icon: BellRinging, testId: "nav-reminders" },
  { to: "/reports", label: "Reports", icon: ChartLine, testId: "nav-reports" },
  { to: "/consolidation", label: "Consolidation", icon: StackSimple, testId: "nav-consolidation" },
  { to: "/intercompany", label: "Intercompany Loans", icon: Handshake, testId: "nav-intercompany" },
  { to: "/sales", label: "Sales Data", icon: UserCircleGear, testId: "nav-sales" },
];

const ADMIN_NAV = [
  { to: "/companies", label: "Companies", icon: Buildings, testId: "nav-companies" },
  { to: "/team", label: "Team", icon: Users, testId: "nav-team" },
  { to: "/subscription", label: "Subscription", icon: Sparkle, testId: "nav-subscription" },
  { to: "/settings", label: "Settings", icon: Gear, testId: "nav-settings" },
];

const SIDEBAR_STYLE = {
  background: "hsl(var(--sidebar))",
  color: "hsl(var(--sidebar-foreground))",
  borderColor: "hsl(var(--sidebar-border))",
};

export default function AppShell() {
  const { user, companies, activeCompany, switchCompany, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar — QuickBooks-style dark forest */}
      <aside
        className="w-64 flex flex-col shrink-0 border-r"
        style={SIDEBAR_STYLE}
      >
        <div className="px-5 py-5" style={{ borderBottom: "1px solid hsl(var(--sidebar-border))" }}>
          <div className="flex items-center gap-2">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ background: "hsl(var(--sidebar-accent))", color: "hsl(var(--sidebar))" }}
            >
              <Sparkle size={18} weight="fill" />
            </div>
            <div>
              <div className="text-lg font-bold leading-none tracking-tight">LedgerLoom</div>
              <div className="text-[10px] uppercase tracking-widest mt-1" style={{ color: "hsl(var(--sidebar-muted))" }}>
                Precision Bookkeeping
              </div>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-2 py-3 space-y-0.5 text-sm overflow-y-auto">
          <SidebarSection label="Ledger" />
          {NAV.map((item) => (
            <NavItem key={item.to} {...item} />
          ))}
          {activeCompany?.vat_number && (
            <NavItem
              to="/mtd"
              label="Making Tax Digital"
              icon={ShieldCheck}
              testId="nav-mtd"
            />
          )}
          <SidebarSection label="Administration" />
          {ADMIN_NAV.map((item) => (
            <NavItem key={item.to} {...item} />
          ))}
          {user?.is_superadmin && (
            <>
              <SidebarSection label="Master Admin" />
              <NavItem
                to="/admin"
                label="Users & Access"
                icon={ShieldStar}
                testId="nav-admin"
              />
            </>
          )}
        </nav>

        <div className="px-4 py-3" style={{ borderTop: "1px solid hsl(var(--sidebar-border))" }}>
          <div className="text-[10px] uppercase tracking-widest" style={{ color: "hsl(var(--sidebar-muted))" }}>
            Signed in as
          </div>
          <div className="text-sm truncate mt-0.5">{user?.email}</div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b border-border bg-card flex items-center justify-between px-6 shrink-0">
          <div className="flex items-center gap-3">
            <div className="overline">Company</div>
            <Select
              value={activeCompany?.id || ""}
              onValueChange={(v) => switchCompany(v)}
            >
              <SelectTrigger
                data-testid="company-switcher"
                className="w-64 h-9 rounded-md text-sm border-border"
              >
                <SelectValue placeholder="Select company" />
              </SelectTrigger>
              <SelectContent>
                {companies.map((c) => (
                  <SelectItem
                    key={c.id}
                    value={c.id}
                    data-testid={`company-option-${c.id}`}
                  >
                    <span>{c.name}</span>
                    <span className="ml-2 text-xs text-muted-foreground">
                      [{c.role}]
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {activeCompany && (
              <span className="tag-badge">
                {activeCompany.currency}
              </span>
            )}
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="h-9 rounded-md text-sm gap-2"
                data-testid="user-menu"
              >
                {user?.name || user?.email}
                <CaretDown size={14} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-md">
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                {user?.email}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={async () => {
                  await logout();
                  navigate("/login");
                }}
                data-testid="logout-btn"
                className="text-sm"
              >
                <SignOut size={14} className="mr-2" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
      <OnboardingTour />
    </div>
  );
}

function SidebarSection({ label }) {
  return (
    <div
      className="text-[10px] uppercase tracking-widest px-3 pt-4 pb-2 font-semibold"
      style={{ color: "hsl(var(--sidebar-muted))" }}
    >
      {label}
    </div>
  );
}

function NavItem({ to, label, icon: Icon, testId }) {
  return (
    <NavLink
      to={to}
      end={to === "/dashboard"}
      data-testid={testId}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-md transition-colors duration-150 relative ${
          isActive ? "sb-active" : "sb-idle"
        }`
      }
      style={({ isActive }) => ({
        background: isActive ? "hsl(var(--sidebar-active-bg))" : "transparent",
        color: isActive ? "hsl(var(--sidebar-foreground))" : "hsl(var(--sidebar-muted))",
      })}
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span
              className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-r"
              style={{ background: "hsl(var(--sidebar-accent))" }}
            />
          )}
          <Icon size={16} weight={isActive ? "fill" : "regular"} />
          <span className="text-[13px] font-medium">{label}</span>
        </>
      )}
    </NavLink>
  );
}
